import { Router, type IRouter } from "express";
import { db, dealsTable, usersTable } from "@workspace/db";
import { eq, and, gte, lte, sql } from "drizzle-orm";
import { CreateDealBody, UpdateDealBody } from "@workspace/api-zod";
import { requireAuth, requireRole } from "../middlewares/auth";

const router: IRouter = Router();

function calcDiscount(asking: number, market: number): number {
  return parseFloat((((market - asking) / market) * 100).toFixed(2));
}

function calcUrgency(discount: number, timeline: string | undefined | null, urgencyReason: string | undefined | null): number {
  let score = 0;
  score += Math.min(discount * 2, 50);
  if (timeline) {
    const days = parseInt(timeline);
    if (!isNaN(days)) {
      if (days <= 7) score += 30;
      else if (days <= 14) score += 25;
      else if (days <= 21) score += 20;
      else if (days <= 30) score += 10;
    }
  }
  if (urgencyReason) score += 20;
  return Math.min(Math.round(score), 100);
}

function formatDeal(deal: typeof dealsTable.$inferSelect, submittedBy?: typeof usersTable.$inferSelect | null) {
  return {
    id: deal.id,
    title: deal.title,
    area: deal.area,
    building: deal.building,
    unitType: deal.unitType,
    bedrooms: deal.bedrooms,
    bathrooms: deal.bathrooms,
    sizeSqft: deal.sizeSqft ? parseFloat(deal.sizeSqft) : null,
    askingPrice: parseFloat(deal.askingPrice),
    estimatedMarketPrice: parseFloat(deal.estimatedMarketPrice),
    discountPercentage: deal.discountPercentage ? parseFloat(deal.discountPercentage) : null,
    roiYield: deal.roiYield ? parseFloat(deal.roiYield) : null,
    urgencyScore: deal.urgencyScore,
    urgencyReason: deal.urgencyReason,
    sellerTimeline: deal.sellerTimeline,
    propertyStatus: deal.propertyStatus,
    paymentPlanDetails: deal.paymentPlanDetails,
    brokerContact: deal.brokerContact,
    imageUrl: deal.imageUrl,
    imageUrls: deal.imageUrls ?? null,
    documentUrls: deal.documentUrls ?? null,
    developer: deal.developer ?? null,
    handoverStatus: deal.handoverStatus ?? null,
    sellerType: deal.sellerType ?? null,
    dealCategory: deal.dealCategory ?? null,
    description: deal.description ?? null,
    visibility: deal.visibility ?? null,
    submittedByUserId: deal.submittedByUserId,
    status: deal.status,
    isVipOnly: deal.isVipOnly,
    aiDistressScore: deal.aiDistressScore ?? null,
    aiDealScore: deal.aiDealScore ?? null,
    aiInvestorAppeal: deal.aiInvestorAppeal ?? null,
    aiRiskLevel: deal.aiRiskLevel ?? null,
    aiRecommendedAction: deal.aiRecommendedAction ?? null,
    aiInvestorSummary: deal.aiInvestorSummary ?? null,
    aiRiskFlags: deal.aiRiskFlags ?? null,
    aiRecommendedBuyerType: deal.aiRecommendedBuyerType ?? null,
    aiLiquidityRating: deal.aiLiquidityRating ?? null,
    aiNegotiationAngle: deal.aiNegotiationAngle ?? null,
    aiMissingInfo: deal.aiMissingInfo ?? null,
    aiPricingComment: deal.aiPricingComment ?? null,
    aiAdminDecision: deal.aiAdminDecision ?? null,
    createdAt: deal.createdAt,
    submittedBy: submittedBy
      ? {
          id: submittedBy.id,
          name: submittedBy.name,
          email: submittedBy.email,
          phone: submittedBy.phone,
          role: submittedBy.role,
          membership: submittedBy.membership,
          stripeCustomerId: submittedBy.stripeCustomerId,
          createdAt: submittedBy.createdAt,
        }
      : null,
  };
}

router.get("/deals", requireAuth, async (req, res): Promise<void> => {
  const { auth } = req;
  const { area, minDiscount, minUrgency, maxPrice, unitType, status } = req.query as Record<string, string | undefined>;

  let query = db
    .select({ deal: dealsTable, user: usersTable })
    .from(dealsTable)
    .leftJoin(usersTable, eq(dealsTable.submittedByUserId, usersTable.id))
    .$dynamic();

  const conditions = [];

  if (auth!.role === "investor") {
    conditions.push(eq(dealsTable.status, "approved"));
  } else if (auth!.role === "broker") {
    conditions.push(eq(dealsTable.submittedByUserId, auth!.userId));
  } else if (status) {
    conditions.push(eq(dealsTable.status, status as "draft" | "pending" | "approved" | "rejected"));
  }

  if (area) conditions.push(eq(dealsTable.area, area));
  if (unitType) conditions.push(eq(dealsTable.unitType, unitType));
  if (minDiscount) conditions.push(gte(sql`CAST(${dealsTable.discountPercentage} AS FLOAT)`, parseFloat(minDiscount)));
  if (minUrgency) conditions.push(gte(dealsTable.urgencyScore, parseInt(minUrgency)));
  if (maxPrice) conditions.push(lte(sql`CAST(${dealsTable.askingPrice} AS FLOAT)`, parseFloat(maxPrice)));

  if (conditions.length > 0) {
    query = query.where(and(...conditions)) as typeof query;
  }

  const rows = await query;
  const deals = rows.map((r) => formatDeal(r.deal, r.user));
  res.json(deals);
});

router.post("/deals", requireAuth, requireRole("broker", "admin"), async (req, res): Promise<void> => {
  const parsed = CreateDealBody.safeParse(req.body);
  if (!parsed.success) {
    res.status(400).json({ error: parsed.error.message });
    return;
  }

  const data = parsed.data;
  const asking = parseFloat(String(data.askingPrice));
  const market = parseFloat(String(data.estimatedMarketPrice));
  const discountPct = calcDiscount(asking, market);
  const urgency = calcUrgency(discountPct, data.sellerTimeline, data.urgencyReason);

  // Admin can explicitly set status (draft/pending/approved); brokers always start at pending
  let initialStatus: "draft" | "pending" | "approved" | "rejected" = "pending";
  if (req.auth!.role === "admin" && data.status) {
    initialStatus = data.status as "draft" | "pending" | "approved" | "rejected";
  }

  const [deal] = await db
    .insert(dealsTable)
    .values({
      title: data.title,
      area: data.area,
      building: data.building ?? null,
      unitType: data.unitType ?? null,
      bedrooms: data.bedrooms ?? null,
      bathrooms: data.bathrooms ?? null,
      sizeSqft: data.sizeSqft != null ? String(data.sizeSqft) : null,
      askingPrice: String(asking),
      estimatedMarketPrice: String(market),
      discountPercentage: String(discountPct),
      roiYield: data.roiYield != null ? String(data.roiYield) : null,
      urgencyScore: urgency,
      urgencyReason: data.urgencyReason ?? null,
      sellerTimeline: data.sellerTimeline ?? null,
      propertyStatus: data.propertyStatus ?? null,
      paymentPlanDetails: data.paymentPlanDetails ?? null,
      brokerContact: data.brokerContact ?? null,
      imageUrl: data.imageUrl ?? null,
      imageUrls: data.imageUrls ?? null,
      documentUrls: data.documentUrls ?? null,
      developer: data.developer ?? null,
      handoverStatus: data.handoverStatus ?? null,
      sellerType: data.sellerType ?? null,
      dealCategory: data.dealCategory ?? null,
      description: data.description ?? null,
      visibility: data.visibility ?? "both",
      isVipOnly: data.isVipOnly ?? false,
      submittedByUserId: req.auth!.userId,
      status: initialStatus,
    })
    .returning();

  const [user] = await db.select().from(usersTable).where(eq(usersTable.id, req.auth!.userId));
  res.status(201).json(formatDeal(deal, user));
});

router.get("/deals/:id", requireAuth, async (req, res): Promise<void> => {
  const raw = Array.isArray(req.params.id) ? req.params.id[0] : req.params.id;
  const id = parseInt(raw, 10);
  if (isNaN(id)) {
    res.status(400).json({ error: "Invalid deal id" });
    return;
  }

  const [row] = await db
    .select({ deal: dealsTable, user: usersTable })
    .from(dealsTable)
    .leftJoin(usersTable, eq(dealsTable.submittedByUserId, usersTable.id))
    .where(eq(dealsTable.id, id));

  if (!row) {
    res.status(404).json({ error: "Deal not found" });
    return;
  }

  const { auth } = req;

  if (auth!.role === "broker" && row.deal.submittedByUserId !== auth!.userId) {
    res.status(403).json({ error: "Access denied" });
    return;
  }

  if (auth!.role === "investor" && row.deal.status !== "approved") {
    res.status(403).json({ error: "Access denied" });
    return;
  }

  res.json(formatDeal(row.deal, row.user));
});

router.put("/deals/:id", requireAuth, async (req, res): Promise<void> => {
  const raw = Array.isArray(req.params.id) ? req.params.id[0] : req.params.id;
  const id = parseInt(raw, 10);

  const parsed = UpdateDealBody.safeParse(req.body);
  if (!parsed.success) {
    res.status(400).json({ error: parsed.error.message });
    return;
  }

  const { auth } = req;

  const [existing] = await db.select().from(dealsTable).where(eq(dealsTable.id, id));
  if (!existing) {
    res.status(404).json({ error: "Deal not found" });
    return;
  }

  if (auth!.role === "broker") {
    if (existing.submittedByUserId !== auth!.userId) {
      res.status(403).json({ error: "Access denied" });
      return;
    }
  }

  const data = parsed.data;
  const updateData: Record<string, unknown> = { ...data };

  if (auth!.role === "broker") {
    delete updateData.status;
    delete updateData.isVipOnly;
  }

  if (data.askingPrice != null && data.estimatedMarketPrice != null) {
    const asking = parseFloat(String(data.askingPrice));
    const market = parseFloat(String(data.estimatedMarketPrice));
    updateData.discountPercentage = String(calcDiscount(asking, market));
    updateData.urgencyScore = calcUrgency(
      calcDiscount(asking, market),
      data.sellerTimeline,
      data.urgencyReason
    );
  }

  const [deal] = await db.update(dealsTable).set(updateData).where(eq(dealsTable.id, id)).returning();

  if (!deal) {
    res.status(404).json({ error: "Deal not found" });
    return;
  }

  const [user] = deal.submittedByUserId
    ? await db.select().from(usersTable).where(eq(usersTable.id, deal.submittedByUserId))
    : [];
  res.json(formatDeal(deal, user));
});

router.delete("/deals/:id", requireAuth, requireRole("admin"), async (req, res): Promise<void> => {
  const raw = Array.isArray(req.params.id) ? req.params.id[0] : req.params.id;
  const id = parseInt(raw, 10);

  const [deal] = await db.delete(dealsTable).where(eq(dealsTable.id, id)).returning();
  if (!deal) {
    res.status(404).json({ error: "Deal not found" });
    return;
  }
  res.sendStatus(204);
});

router.post("/deals/:id/approve", requireAuth, requireRole("admin"), async (req, res): Promise<void> => {
  const raw = Array.isArray(req.params.id) ? req.params.id[0] : req.params.id;
  const id = parseInt(raw, 10);

  const [deal] = await db
    .update(dealsTable)
    .set({ status: "approved" })
    .where(eq(dealsTable.id, id))
    .returning();

  if (!deal) {
    res.status(404).json({ error: "Deal not found" });
    return;
  }

  const [user] = deal.submittedByUserId
    ? await db.select().from(usersTable).where(eq(usersTable.id, deal.submittedByUserId))
    : [];
  res.json(formatDeal(deal, user));
});

router.post("/deals/:id/reject", requireAuth, requireRole("admin"), async (req, res): Promise<void> => {
  const raw = Array.isArray(req.params.id) ? req.params.id[0] : req.params.id;
  const id = parseInt(raw, 10);

  const [deal] = await db
    .update(dealsTable)
    .set({ status: "rejected" })
    .where(eq(dealsTable.id, id))
    .returning();

  if (!deal) {
    res.status(404).json({ error: "Deal not found" });
    return;
  }

  const [user] = deal.submittedByUserId
    ? await db.select().from(usersTable).where(eq(usersTable.id, deal.submittedByUserId))
    : [];
  res.json(formatDeal(deal, user));
});

router.post("/deals/:id/deactivate", requireAuth, requireRole("admin"), async (req, res): Promise<void> => {
  const raw = Array.isArray(req.params.id) ? req.params.id[0] : req.params.id;
  const id = parseInt(raw, 10);

  const [deal] = await db
    .update(dealsTable)
    .set({ status: "pending" })
    .where(eq(dealsTable.id, id))
    .returning();

  if (!deal) {
    res.status(404).json({ error: "Deal not found" });
    return;
  }

  const [user] = deal.submittedByUserId
    ? await db.select().from(usersTable).where(eq(usersTable.id, deal.submittedByUserId))
    : [];
  res.json(formatDeal(deal, user));
});

export default router;
